import { TakeoverId } from 'takeovers/takeoverRegistry'

/**
 * Type guard to check if a payload is an App.LabHeroCarouselPayload.
 * Validates that the payload is an object with a 'type' property equal to 'heroCarousel'.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabHeroCarouselPayload structure, false otherwise.
 */
export const isHeroBannerPayload = (payload: unknown): payload is App.LabHeroCarouselPayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'heroCarousel'
  )
}

/**
 * Type guard to check if a payload is an App.LabBannerMessagePayload.
 * Validates that the payload is an object with a 'type' property equal to 'bannerMessage'.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabBannerMessagePayload structure, false otherwise.
 */
export const isBannerMessagePayload = (payload: unknown): payload is App.LabBannerMessagePayload => {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    (payload as { type?: unknown }).type === 'bannerMessage'
  )
}

/**
 * Type guard to check if a payload is an App.LabSiteTakeoverPayload.
 * Validates that the payload is an object with 'type' property equal to 'siteTakeover'
 * and an 'id' property that matches a known TakeoverId from the registry.
 *
 * @param payload - The payload to check. Typed as `unknown` because it comes from external sources
 *                  (API responses, user input, etc.) where the type cannot be guaranteed at compile time.
 *                  Using `unknown` forces runtime validation and provides type safety.
 * @returns True if the payload matches the LabSiteTakeoverPayload structure and has a valid takeover ID, false otherwise.
 */
export const isLabSiteTakeoverPayload = (payload: unknown): payload is App.LabSiteTakeoverPayload => {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('type' in payload) ||
    !('id' in payload) ||
    (payload as { type?: unknown }).type !== 'siteTakeover' ||
    typeof (payload as { id?: unknown }).id !== 'string'
  ) {
    return false
  }

  // Validate that the id is a known takeover ID
  const id = (payload as { id: string }).id
  return Object.values(TakeoverId).includes(id as TakeoverId)
}
